// Etapas e campos do formulário do GH Experience, na ordem definida pela equipe.
// Rótulos, placeholders e mensagens de erro são chaves de tradução (ghxp_*).

export const DEFAULT_FORM_DATA = {
  name: "",
  email: "",
  phone: "",
  birthDate: "",
  address: "",
  zipCode: "",
  children: "",
  team: "",
};

export const CHILDREN_OPTIONS = [
  { value: "0", label: "0" },
  { value: "1", label: "1" },
  { value: "2", label: "2" },
  { value: "3", label: "3" },
  { value: "4", label: "4" },
  { value: "5+", labelKey: "ghxp_children_5plus" },
];

export function maskPhone(value) {
  const digits = value.replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 2) return digits.length ? `(${digits}` : "";
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

export function maskZipCode(value) {
  const digits = value.replace(/\D/g, "").slice(0, 8);
  return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
}

// Em inglês e espanhol o convidado pode ser de fora do Brasil: telefone livre e código postal sem a máscara do CEP.
// O código postal aceita só dígitos (3 a 8): a coluna Cep do banco é numérica.
const maskIntlPhone = (value) => value.replace(/[^\d\s()+-]/g, "").slice(0, 20);
const maskIntlZipCode = (value) => value.replace(/[^\d\s-]/g, "").slice(0, 10);

const isBrazil = (lang) => lang === "pt";

export const STEPS = [
  {
    titleKey: "ghxp_step_details",
    fields: [
      { name: "name", labelKey: "ghxp_field_name", type: "text", autoComplete: "name", maxLength: 100 },
      { name: "email", labelKey: "ghxp_field_email", type: "email", autoComplete: "email", maxLength: 100 },
    ],
  },
  {
    titleKey: "ghxp_step_contact",
    fields: [
      {
        name: "phone",
        labelKey: "ghxp_field_phone",
        type: "tel",
        autoComplete: "tel",
        inputMode: "tel",
        placeholderKey: "ghxp_placeholder_phone",
        mask: (value, lang) => (isBrazil(lang) ? maskPhone(value) : maskIntlPhone(value)),
      },
      // max (hoje) também limita o ano a 4 dígitos no seletor de data do Chrome.
      {
        name: "birthDate",
        labelKey: "ghxp_field_birthDate",
        type: "date",
        autoComplete: "bday",
        min: "1900-01-01",
        max: () => new Date().toISOString().slice(0, 10),
      },
    ],
  },
  // Endereço é opcional: a etapa pode ser pulada; se preenchido, continua sendo validado.
  {
    titleKey: "ghxp_step_address",
    wide: true,
    optional: true,
    fields: [
      {
        name: "address",
        labelKey: "ghxp_field_address",
        type: "text",
        autoComplete: "street-address",
        maxLength: 160,
        placeholderKey: "ghxp_placeholder_address",
      },
      {
        name: "zipCode",
        labelKey: "ghxp_field_zipCode",
        type: "text",
        autoComplete: "postal-code",
        inputMode: (lang) => (isBrazil(lang) ? "numeric" : "text"),
        placeholderKey: "ghxp_placeholder_zipCode",
        mask: (value, lang) => (isBrazil(lang) ? maskZipCode(value) : maskIntlZipCode(value)),
      },
    ],
  },
  {
    titleKey: "ghxp_step_about",
    fields: [
      { name: "children", labelKey: "ghxp_field_children", type: "select", options: CHILDREN_OPTIONS },
      { name: "team", labelKey: "ghxp_field_team", type: "text", maxLength: 60 },
    ],
  },
];

const VALIDATORS = {
  name: (v) => v.trim().split(/\s+/).length < 2 || v.trim().length < 5,
  email: (v) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim()),
  phone: (v, lang) => {
    const digits = v.replace(/\D/g, "").length;
    return isBrazil(lang) ? digits < 10 : digits < 8 || digits > 15;
  },
  birthDate: (v) => !v || v < "1900-01-01" || v > new Date().toISOString().slice(0, 10),
  // Opcionais: vazio é válido.
  address: (v) => !!v.trim() && v.trim().length < 5,
  zipCode: (v, lang) => {
    if (!v.trim()) return false;
    const digits = v.replace(/\D/g, "").length;
    return isBrazil(lang) ? digits !== 8 : digits < 3 || digits > 8;
  },
  children: (v) => !v,
  team: (v) => !v.trim(),
};

// Reaplica as máscaras ao trocar de idioma (as regras BR x internacional mudam com ele).
export function applyMasks(data, lang) {
  return STEPS.flatMap((step) => step.fields).reduce(
    (result, field) => (field.mask && result[field.name] ? { ...result, [field.name]: field.mask(result[field.name], lang) } : result),
    data
  );
}

// Campos de uma etapa opcional ficam vazios ao pular a etapa.
export function clearFields(data, fieldNames) {
  return fieldNames.reduce((result, name) => ({ ...result, [name]: DEFAULT_FORM_DATA[name] }), data);
}

// Retorna { campo: chave da mensagem } apenas para os campos informados que estão inválidos.
export function validateFields(data, fieldNames, lang) {
  return fieldNames.reduce(
    (errors, name) => (VALIDATORS[name](data[name], lang) ? { ...errors, [name]: `ghxp_error_${name}` } : errors),
    {}
  );
}
